import { siteConfig } from './siteConfig.js'

export type CourseSeoSection = {
  heading: string
  body?: string
  items?: string[]
}

export type CourseSeoFaq = {
  question: string
  answer: string
}

export type CourseSeoContent = {
  slug: string
  seoTitle: string
  seoDescription: string
  introAnswer: string
  sections: CourseSeoSection[]
  faqs: CourseSeoFaq[]
}

export const priorityCourseSeoContent: CourseSeoContent[] = [
  {
    slug: 'ai-agent-builder-beginner-to-pro',
    seoTitle: 'AI Agent Builder Course in Bangladesh | Skillo Academy',
    seoDescription:
      'Join Skillo Academy’s 12-week AI Agent Builder course in Bangladesh. Learn practical AI agent skills with n8n, APIs, RAG, WhatsApp agents, and voice AI in Bangla + English — for studies, projects, and career growth.',
    introAnswer:
      'AI Agent Builder: From Beginner to Pro is a 12-week live skill-building course by Skillo Academy for students and beginners in Bangladesh who want practical AI automation skills — n8n workflows, APIs, webhooks, RAG, WhatsApp AI agents, voice AI agents, and portfolio projects they can apply to education, personal work, employment, startups, or freelancing depending on their goals and effort.',
    sections: [
      {
        heading: 'Who This Course Is For',
        items: [
          'Students and beginners who want a practical AI course in Bangladesh without starting from heavy coding.',
          'University learners who want an academic and career advantage through modern AI agent skills and portfolio demos.',
          'Junior developers, career switchers, and curious builders who want industry-relevant automation skills for projects and future opportunities.',
        ],
      },
      {
        heading: 'What Students Learn',
        items: [
          'AI agent fundamentals, prompt engineering, API usage, webhooks, JSON, and workflow logic.',
          'n8n, Make, Zapier, Airtable, Google Sheets, WhatsApp, Telegram, OpenAI, Claude, Gemini, LangChain, LangGraph, RAG, and vector database concepts.',
          'How to document demos, present portfolio projects, and communicate automation work clearly — skills useful for studies, jobs, startups, or optional freelancing.',
        ],
      },
      {
        heading: 'Projects Students Build',
        items: [
          'Restaurant WhatsApp booking bot for reservations, menu questions, and confirmations.',
          'Clinic appointment AI agent for booking, reminders, and patient FAQ handling.',
          'Lead qualification workflow that captures, scores, and routes business leads automatically.',
        ],
      },
      {
        heading: 'Requirements And Support',
        body: 'Students should be comfortable using a computer, reading basic English documentation, and practicing consistently. Classes are mentor-led in Bangla + English with WhatsApp support and portfolio guidance. Results after the course depend on dedication, practice, communication, and market demand.',
      },
      {
        heading: 'Income Disclaimer',
        body: siteConfig.trust.incomeDisclaimer,
      },
    ],
    faqs: [
      {
        question: 'What is an AI Agent?',
        answer:
          'An AI agent is a software workflow that can use AI models, tools, memory, APIs, and business rules to complete tasks such as answering customers, booking appointments, qualifying leads, or preparing reports.',
      },
      {
        question: 'Can I build AI agents without coding?',
        answer:
          'Yes. This course starts with low-code and no-code workflow tools like n8n, then gradually introduces APIs, structured data, and advanced agent concepts so beginners can build useful automations step by step.',
      },
      {
        question: 'Is this a freelancing or income-guarantee course?',
        answer:
          'No. This is a skill-building course. Freelancing is one optional path after you learn the skills — alongside studies, jobs, startups, and personal projects. Skillo does not guarantee clients, jobs, or earnings. Results depend on practice, portfolio quality, communication, consistency, and market demand.',
      },
      {
        question: 'What is RAG?',
        answer:
          'RAG means retrieval-augmented generation. It lets an AI system answer using your own documents, knowledge base, or data instead of relying only on the model’s general training.',
      },
      {
        question: 'AI Agent Builder vs Full Stack Development - which should I learn first?',
        answer:
          'Choose AI Agent Builder first if you want practical automation and AI agent skills with a low-code start. Choose Full Stack Development first if you want stronger coding foundations to build complete websites, apps, SaaS products, and APIs. Both can support career growth; neither guarantees income.',
      },
    ],
  },
  {
    slug: 'full-stack-ai-web-development',
    seoTitle: 'Full Stack Web Development Course in Bangladesh | Skillo Academy',
    seoDescription:
      'Learn Full Stack Web Development with AI in Bangladesh. Skillo Academy teaches HTML, CSS, Tailwind, JavaScript, TypeScript, React, Next.js, Node.js, MongoDB, auth, payments, deployment, and AI API integration.',
    introAnswer:
      'Full Stack Web Development with AI Integration is a 24-week live course of 72 classes by Skillo Academy for beginners who want to learn modern web development in Bangla + English and build websites, apps, SaaS projects, APIs, and AI-powered features.',
    sections: [
      {
        heading: 'Who This Course Is For',
        items: [
          'Students who want a web development course in Sylhet or Bangladesh with a modern stack.',
          'Beginners who want to learn coding seriously and build portfolio projects for jobs or freelancing.',
          'Learners who want React, Next.js, Node.js, MongoDB, deployment, payments, authentication, and AI integration in one path.',
        ],
      },
      {
        heading: 'What Students Learn',
        items: [
          'HTML, CSS, Tailwind CSS, JavaScript, TypeScript, React, Next.js, Node.js, Express, MongoDB, and Git.',
          'Authentication, forms, API design, file handling, payments, deployment, environment variables, domains, and SSL basics.',
          'How to integrate OpenAI APIs and build useful AI-powered product features.',
        ],
      },
      {
        heading: 'Projects Students Build',
        items: [
          'Responsive business website with WhatsApp CTA, contact flow, and live deployment.',
          'E-commerce or booking app with authentication, admin dashboard, and payment integration.',
          'AI-powered SaaS dashboard with a working AI feature and public portfolio demo.',
        ],
      },
      {
        heading: 'Requirements And Support',
        body: 'Students should have basic computer confidence, a laptop or desktop, and willingness to practice coding each week. Classes are mentor-led with project feedback and portfolio guidance.',
      },
      {
        heading: 'Income Disclaimer',
        body: siteConfig.trust.incomeDisclaimer,
      },
    ],
    faqs: [
      {
        question: 'What is Full Stack Web Development?',
        answer:
          'Full stack web development means building both the frontend users see and the backend systems that handle data, APIs, authentication, payments, and business logic.',
      },
      {
        question: 'Is web development still worth learning?',
        answer:
          'Yes. Web development remains useful because businesses still need websites, dashboards, SaaS products, e-commerce systems, booking systems, internal tools, and AI-powered web apps.',
      },
      {
        question: 'Should I learn React or Next.js first?',
        answer:
          'Learn React fundamentals first, then Next.js. Next.js builds on React and adds routing, rendering, backend-like features, deployment patterns, and production app structure.',
      },
      {
        question: 'Can I learn web development in Bangla?',
        answer:
          'Yes. Skillo teaches in Bangla + English so beginners can understand the concepts while also learning the English terms used in documentation and real projects.',
      },
      {
        question: 'Do I need math to learn web development?',
        answer:
          'You do not need advanced math to start web development. Logic, consistency, problem solving, and practice matter much more at the beginner stage.',
      },
    ],
  },
  {
    slug: 'basic-computer-english',
    seoTitle: 'Basic Computer & English Course in Sylhet | Skillo Academy',
    seoDescription:
      'Skillo Academy’s Basic Computer & English course in Sylhet helps absolute beginners learn computer basics, internet, Gmail, Google Workspace, typing, practical English, online communication, and safe internet habits — a foundation for later tech courses.',
    introAnswer:
      'Basic Computer & English for Tech is a beginner-friendly foundation course for students who want computer confidence, internet skills, email, Google Workspace, typing, practical English, and safe habits before joining AI, web, or other Skillo programs.',
    sections: [
      {
        heading: 'Who This Course Is For',
        items: [
          'Absolute beginners in Sylhet who are not yet confident using a computer, browser, email, files, and online tools.',
          'Students preparing for AI, web development, design, digital marketing, or other Skillo courses.',
          'Learners who need practical English for apps, websites, tutorials, messages, and basic professional communication.',
        ],
      },
      {
        heading: 'What Students Learn',
        items: [
          'Computer parts, desktop navigation, files, folders, typing, browser use, downloads, bookmarks, Gmail, and attachments.',
          'Google Drive, Docs, Sheets, Slides, WhatsApp, Zoom, screenshots, screen recording, account setup, and password safety.',
          'Tech vocabulary, app instructions, simple English messages, email writing, and confidence using English-interface tools.',
        ],
      },
      {
        heading: 'Projects Students Complete',
        items: [
          'Create and organize documents, sheets, slides, email attachments, and shared folders.',
          'Prepare a simple CV, send professional messages, and complete online account setup tasks safely.',
          'Complete practical real-world tasks that prepare students for the next Skillo technical course.',
        ],
      },
      {
        heading: 'Important Note',
        body: 'This is a foundation course, not a direct income guarantee. It prepares students for online learning, beginner digital work, and future Skillo tech courses.',
      },
      {
        heading: 'Why the Price Is Not Lower',
        body: 'The fee covers a live 6-week guided program with repeated practice, checking, correction, and beginner-friendly support. Students are not only buying information; they are paying for confidence with a computer, practical English, Google Workspace, safe internet habits, and a smoother path into advanced Skillo courses.',
      },
      {
        heading: 'Income Disclaimer',
        body: siteConfig.trust.incomeDisclaimer,
      },
    ],
    faqs: [
      {
        question: 'Can I join if I know almost nothing about computers?',
        answer:
          'Yes. This course is designed for absolute beginners and starts from computer parts, files, folders, typing, browser use, email, and safe internet habits.',
      },
      {
        question: 'Is this a freelancing course?',
        answer:
          'It is a foundation course for digital readiness. Students learn the computer, English, and online communication basics needed before joining more advanced AI, web, design, or marketing skill courses.',
      },
      {
        question: 'Why does this course cost ৳2,999?',
        answer:
          'The regular fee is ৳4,999, and the current early-bird fee is ৳2,999, so students save ৳2,000. The price covers 6 weeks of live classes, hands-on practice, checking, correction, beginner support, and practical English/computer confidence that recorded tutorials usually do not provide.',
      },
      {
        question: 'Will this course help me join AI or web development later?',
        answer:
          'Yes. The course is designed as a pathway into Skillo’s AI Agent Builder, Full Stack Web Development, design, marketing, and other technical programs.',
      },
      {
        question: 'Do I need English before joining?',
        answer:
          'No advanced English is required. The course teaches practical English for apps, websites, tech instructions, messages, and online learning.',
      },
      {
        question: 'Do I need my own laptop?',
        answer:
          'Having regular access to a laptop or desktop is strongly recommended because confidence comes from repeated hands-on practice.',
      },
    ],
  },
]

export function getPriorityCourseSeo(slug: string) {
  return priorityCourseSeoContent.find((content) => content.slug === slug)
}
